import {
  Box,
  Grid,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  BulkSendSettingsStatus,
  OrganizationBulkSendSettings,
  OrganizationNiche,
  OrganizationNicheStatus,
  VAStatus,
} from "schemas/dashboard";
import { routes } from "schemas/routes";

const styles = {
  subtitle: {
    fontSize: { xs: 12, md: 18 },
  },
};

interface Props {
  bulkSendSetting: OrganizationBulkSendSettings;
  niches: OrganizationNiche[];
  clickToGeneralSettings?: () => void;
}

const VirtualAssistantStatus = ({
  bulkSendSetting,
  niches,
  clickToGeneralSettings,
}: Props) => {
  const { currentOrg } = useContext(OrganizationUserContext);
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const activeNiches = niches.filter(
    (niche) => niche.status === OrganizationNicheStatus.active,
  );
  const pendingNiches = niches.filter(
    (n) => n.status === OrganizationNicheStatus.pending,
  );

  const totalBrandsScheduled = bulkSendSetting?.totalBrands;

  const status =
    bulkSendSetting.status === BulkSendSettingsStatus.active &&
    activeNiches.length > 0
      ? "live 🚀"
      : bulkSendSetting.status === BulkSendSettingsStatus.inactive ||
          pendingNiches.length === 0
        ? "paused 👀"
        : "getting set-up 🚀";

  return (
    <Box
      display="flex"
      flexWrap="wrap"
      gap={3}
      sx={{ mb: 2, justifyContent: { xs: "flex-end", sm: "space-between" } }}
      alignItems="flex-start"
    >
      <Grid item xs={12} md>
        <Typography
          sx={{
            fontWeight: 600,
            width: { xs: "100%", md: "inherit" },
            fontSize: { xs: 20, md: 40 },
          }}
        >
          Your AI Virtual Assistant is {status}
        </Typography>
        {status === "live 🚀" && (
          <Typography sx={styles.subtitle}>
            Campaigns are running smoothly —{" "}
            <Typography
              component="span"
              sx={{ fontWeight: 600, fontSize: "inherit" }}
            >
              {activeNiches.length} niche
              {activeNiches.length !== 1 ? "s" : ""} active,{" "}
              {totalBrandsScheduled} emails to be{" "}
              <Link
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  navigate(`/${routes.inboxScheduled}`);
                }}
              >
                scheduled for next week
              </Link>
            </Typography>{" "}
          </Typography>
        )}
        {status === "getting set-up 🚀" && (
          <Typography sx={styles.subtitle}>
            The set-up process is running smoothly—{" "}
            <Typography
              component="span"
              sx={{ fontWeight: 600, fontSize: "inherit" }}
            >
              {pendingNiches.length} niche
              {pendingNiches.length !== 1 ? "s" : ""} are getting set up
            </Typography>
          </Typography>
        )}
        {status === "paused 👀" &&
          bulkSendSetting.status === BulkSendSettingsStatus.inactive && (
            <Typography sx={styles.subtitle}>
              {currentOrg?.vaStatus === VAStatus.onboarding_paused ? (
                <>
                  Currently, your AI Assistant is paused until onboarding is
                  complete.
                </>
              ) : (
                <>
                  Currently, your AI Assistant is paused. You can change this{" "}
                  {isMobileScreen ? (
                    "by scrolling down to pause brand outreach"
                  ) : (
                    <>
                      in your{" "}
                      <Link
                        sx={{
                          fontWeight: 600,
                          cursor: "pointer",
                          textDecoration: "none",
                          color: "purple.dark",
                        }}
                        onClick={clickToGeneralSettings}
                      >
                        General Settings
                      </Link>
                    </>
                  )}
                  .
                </>
              )}
            </Typography>
          )}
        {status === "paused 👀" &&
          bulkSendSetting.status !== BulkSendSettingsStatus.inactive && (
            <Typography sx={styles.subtitle}>
              Currently, you have no active niches. Set a niche below as active
              to start scheduling emails
            </Typography>
          )}
      </Grid>
    </Box>
  );
};

export default VirtualAssistantStatus;
